import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { BackLink } from "@/components/BackLink";
import { SupportConsolePageHeader } from "@/components/SupportConsoleLayout";
import {
  agentJoinCall,
  type CallPayload,
  useAgentAssign,
  useAgentReply,
  useAgentStatus,
  useAgentTicket,
} from "@/lib/support";
import { SupportCallRoom } from "@/modules/support/SupportCallRoom";
import { TicketThread } from "@/modules/support/TicketThread";

const STATUSES = ["open", "pending_agent", "pending_customer", "resolved", "closed"];

export default function SupportAgentTicketPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const { data, isLoading } = useAgentTicket(id);
  const reply = useAgentReply(id);
  const setStatus = useAgentStatus(id);
  const assign = useAgentAssign(id);
  const [draft, setDraft] = useState("");
  const [internal, setInternal] = useState(false);
  const [call, setCall] = useState<CallPayload | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    await reply.mutateAsync({ body: draft, is_internal_note: internal });
    setDraft("");
    setInternal(false);
  };

  const join = async () => {
    try {
      setCall(await agentJoinCall(id));
    } catch {
      /* no active call */
    }
  };

  if (isLoading || !data) {
    return (
      <>
        <SupportConsolePageHeader title={t("support.console.ticket.title")} />
        <p className="text-nb-text-2">{t("common.loading")}</p>
      </>
    );
  }
  const { ticket, messages } = data;

  return (
    <>
      {call && <SupportCallRoom call={call} onLeave={() => setCall(null)} />}

      <BackLink fallback="/support-console" className="text-sm text-cvp-accent">
        ← {t("support.console.ticket.back")}
      </BackLink>
      <SupportConsolePageHeader
        title={ticket.subject}
        description={
          `${ticket.org_name} · #${ticket.id.slice(0, 8)} · ` +
          `${t(`support.priority.${ticket.priority}`)} · ` +
          `${t("support.console.ticket.plan")} ${ticket.support_plan_slug} · ` +
          `${t("support.console.ticket.requester")} ` +
          (ticket.requester_name
            ? `${ticket.requester_name} (${ticket.requester_email})`
            : ticket.requester_email ?? "—")
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          className="input max-w-[220px]"
          value={ticket.status}
          onChange={(e) => setStatus.mutate(e.target.value)}
          aria-label={t("support.console.ticket.changeStatus")}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`support.status.${s}`)}</option>
          ))}
        </select>
        {ticket.assignee_email ? (
          <span className="text-sm text-nb-text-2">
            {t("support.console.ticket.assignedTo", { who: ticket.assignee_email })}
          </span>
        ) : (
          <button
            className="btn-secondary"
            disabled={assign.isPending}
            onClick={() => assign.mutate()}
          >
            {t("support.console.ticket.assignToMe")}
          </button>
        )}
        {ticket.live_call_session && (
          <button className="btn-primary" onClick={join}>
            {t("support.console.ticket.join")}
          </button>
        )}
      </div>

      <div className="rounded-lg border border-nb-border p-4">
        <TicketThread messages={messages} />
      </div>

      <form onSubmit={send} className="mt-4 space-y-2">
        <textarea
          className="input"
          rows={3}
          placeholder={
            internal
              ? t("support.console.ticket.internalPlaceholder")
              : t("support.console.ticket.replyPlaceholder")
          }
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={reply.isPending}>
            {reply.isPending
              ? t("support.console.ticket.sending")
              : internal
                ? t("support.console.ticket.addNote")
                : t("support.console.ticket.send")}
          </button>
          <label className="flex items-center gap-1.5 text-sm text-nb-text-2">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            {t("support.console.ticket.internalNote")}
          </label>
        </div>
      </form>
    </>
  );
}
